import { useCallback, useEffect, useMemo } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { ExclamationmarkSmallBubbleIcon } from '@/icons';
import { ANSWER_REVEAL_MODE, ANSWER_REVEAL_MODE_OPTIONS, type AnswerRevealMode, getBuildAnswerRevealModeLabelKey, ROUND_TEMPLATE_TYPE } from '@/shared/utils/answerRevealMode';
import { Tooltip } from '@/ui_v3';
import { SingleSelect } from '@/ui_v3/components/select';
import { useBuildFormContext } from '@/views/build/form/schema';
import { ToggleOptionContainer } from './ToggleOptionContainer';
export const AnswerRevealModeField = () => {
  const {
    t
  } = useTranslation(['build']);
  const {
    watch,
    setValue
  } = useBuildFormContext();
  const answerRevealMode = watch('answerRevealMode');
  const templateType = watch('templateType');
  const isRoundTemplate = templateType === ROUND_TEMPLATE_TYPE;
  // build:answerReveal.* 는 NEW 키 — /translate 스텝이 locale 시트/JSON에 등록한다.
  const roundTemplateTooltip = t('build:answerReveal.roundTemplateTooltip');
  const answerRevealModeOptions = useMemo(() => ANSWER_REVEAL_MODE_OPTIONS.map(value => ({
    label: t(getBuildAnswerRevealModeLabelKey(value)),
    value
  })), [t]);
  const setAnswerRevealMode = useCallback((value: AnswerRevealMode) => {
    setValue('answerRevealMode', value, {
      shouldTouch: true,
      shouldDirty: true
    });
  }, [setValue]);
  const handleAnswerRevealModeChange = (value: AnswerRevealMode | null) => {
    if (!value || isRoundTemplate) return;
    setAnswerRevealMode(value);
  };
  useEffect(() => {
    if (isRoundTemplate && answerRevealMode !== ANSWER_REVEAL_MODE.ALWAYS) {
      setAnswerRevealMode(ANSWER_REVEAL_MODE.ALWAYS);
    }
  }, [answerRevealMode, isRoundTemplate, setAnswerRevealMode]);
  return <div className="flex flex-col gap-sm" data-sentry-component="AnswerRevealModeField" data-sentry-source-file="AnswerRevealModeField.tsx">
      <ToggleOptionContainer optionIcon={<ExclamationmarkSmallBubbleIcon size="sm" fill={isRoundTemplate ? ICON_COLOR.disabled : ICON_COLOR.neutral} />} optionTitle={t('build:answerReveal.title')} optionDescription={t('build:answerReveal.description')} planType="FREE" isBadgeVisible={false} disabled={isRoundTemplate} descriptionPlacement="text" renderToggle={() => {
      const select = <div className="w-full basis-full mobile-lg:w-[200px] mobile-lg:basis-auto" tabIndex={isRoundTemplate ? 0 : undefined} onClick={event => event.stopPropagation()}>
              <SingleSelect value={answerRevealMode} options={answerRevealModeOptions} onValueChange={handleAnswerRevealModeChange} disabled={isRoundTemplate} showSelectedIcon={false} />
            </div>;
      if (!isRoundTemplate) return select;
      return <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>{select}</Tooltip.Trigger>
                <Tooltip.Content>{roundTemplateTooltip}</Tooltip.Content>
              </Tooltip.Root>
            </Tooltip.Provider>;
    }} data-sentry-element="ToggleOptionContainer" data-sentry-source-file="AnswerRevealModeField.tsx" />
    </div>;
};